import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { PresentationalAtomsModule } from '@et/presentational/atoms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  DpDatePickerModule,
  IDatePickerDirectiveConfig,
} from 'ng2-date-picker';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  EtJournalSigner,
  allStates,
  IDENTIFICATION_METHOD_OPTIONS,
  DEFAULT_DATE_PICKER,
  IdentificationMethod,
  AddEtJournalSignerDto,
  PatchEtJournalSignerDto,
} from '@et/typings';
import { transformDate, isJSON, convertToUTCDate } from '@et/utils';

@Component({
  selector: 'et-journal-signer-form',
  imports: [PresentationalAtomsModule, ReactiveFormsModule, DpDatePickerModule],
  templateUrl: './signer-form.component.html',
  styleUrl: './signer-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignerFormComponent implements OnInit, OnChanges {
  @Input() signer!: EtJournalSigner | null;
  @Input() defaultState = '';
  @Input() ipAddress = '';
  @Input() isLoading = false;
  @Input() error: string | null = null;
  @Input() hideButtons = false;

  @Output() payloadSubmitted = new EventEmitter<
    Partial<AddEtJournalSignerDto | PatchEtJournalSignerDto>
  >();

  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly stateOptions = allStates;
  readonly identificationMethodOptions = IDENTIFICATION_METHOD_OPTIONS;
  readonly datePickerConfig: IDatePickerDirectiveConfig = {
    ...DEFAULT_DATE_PICKER,
    format: 'MM/DD/YYYY',
  };

  private identificationLastValue: IdentificationMethod =
    'identification_cards';
  private readonly identificationCache = new Map<
    IdentificationMethod,
    Record<string, string>
  >();

  readonly form = this.fb.nonNullable.group({
    signerName: ['', [Validators.required]],
    signerState: [''],
    signerAddress: [''],

    identificationMethod: ['identification_cards'],

    // identification_cards
    signerIdType: [''],
    signerIdNumber: [''],
    signerUtcIdIssuedOn: [''],
    signerUtcIdExpiresOn: [''],

    // personal_knowledge
    signerPersonalKnowledgeStatement: [''],

    // credible_witnesses
    signerWitnessName: [''],
    signerWitnessIdType: [''],
    signerWitnessIdInfo: [''],
  });

  get isEditMode(): boolean {
    return !!this.signer;
  }

  /**
   * Initializes the form from existing signer data in edit mode, or defaults in add mode,
   * then wires identification-method caching.
   */
  ngOnInit(): void {
    if (this.isEditMode && this.signer) {
      this.patchFormFromSigner(this.signer);
    } else {
      this.patchDefaultsForAdd();
    }

    // Save last identification method
    this.identificationLastValue = this.form.controls.identificationMethod
      .value as IdentificationMethod;
    this.updateIdentificationValues(this.identificationLastValue);

    this.form.controls.identificationMethod.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((method) => {
        this.updateIdentificationValues(method as IdentificationMethod);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['signer']) {
      const signer = changes['signer'].currentValue;
      if (this.isEditMode && signer) {
        this.patchFormFromSigner(signer);
        this.updateIdentificationValues(this.identificationLastValue);
      } else {
        this.patchDefaultsForAdd();
      }
    }
  }

  reset(): void {
    this.form.reset();
    this.identificationCache.clear();
    this.identificationLastValue = 'identification_cards';
    this.updateIdentificationValues(this.identificationLastValue);
  }

  /**
   * Persists field values for the previous identification method in the cache, then restores
   * cached values for the target method or clears method-specific fields when switching to a new method.
   *
   * @param method - Identification method being activated.
   */
  private updateIdentificationValues(method: IdentificationMethod) {
    // Save current values to chache
    this.identificationCache.set(this.identificationLastValue, {
      signerIdType: this.form.controls.signerIdType.value,
      signerIdNumber: this.form.controls.signerIdNumber.value,
      signerUtcIdIssuedOn: this.form.controls.signerUtcIdIssuedOn.value,
      signerUtcIdExpiresOn: this.form.controls.signerUtcIdExpiresOn.value,
      signerPersonalKnowledgeStatement:
        this.form.controls.signerPersonalKnowledgeStatement.value,
      signerWitnessName: this.form.controls.signerWitnessName.value,
      signerWitnessIdType: this.form.controls.signerWitnessIdType.value,
      signerWitnessIdInfo: this.form.controls.signerWitnessIdInfo.value,
    });

    // Load previous values from cache if exists
    const values = this.identificationCache.get(method);

    if (values) {
      this.form.patchValue(values, { emitEvent: false });
    } else {
      // Reset identification values
      this.form.patchValue(
        {
          signerIdType: '',
          signerIdNumber: '',
          signerUtcIdIssuedOn: '',
          signerUtcIdExpiresOn: '',
          signerPersonalKnowledgeStatement: '',
          signerWitnessName: '',
          signerWitnessIdType: '',
          signerWitnessIdInfo: '',
        },
        { emitEvent: false },
      );
    }

    // Update last value
    this.identificationLastValue = method;
  }

  /**
   * Validates the form, then creates or updates the signer via the API and updates local state on success.
   */
  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = this.isEditMode
      ? this.buildPatchSignerPayload()
      : this.buildAddSignerPayload();

    this.payloadSubmitted.emit(payload);
  }

  /**
   * Submits the form without validation.
   * Used when the form is submitted from the journal generator component to save the signer progress.
   */
  onSubmitWithoutValidation(): void {
    const payload = this.isEditMode
      ? this.buildPatchSignerPayload()
      : this.buildAddSignerPayload();

    this.payloadSubmitted.emit(payload);
  }

  showValidationErrors() {
    this.form.markAllAsTouched();
    this.cdr.detectChanges();
  }

  /**
   * Populates the reactive form from an existing journal signer (edit mode).
   *
   * @param signer - Signer entity to bind into the form.
   */
  private patchFormFromSigner(signer: EtJournalSigner): void {
    this.form.patchValue(
      {
        signerName: signer.name ?? '',
        signerState: signer.state ?? '',
        signerAddress: signer.address ?? '',
        identificationMethod:
          signer.identificationMethod as IdentificationMethod,

        signerIdType: signer.idType ?? '',
        signerIdNumber: signer.idNumber ?? '',
        signerUtcIdIssuedOn: signer.utcIdIssuedOn
          ? transformDate(convertToUTCDate(signer.utcIdIssuedOn), 'MM/DD/YYYY')
          : '',
        signerUtcIdExpiresOn: signer.utcIdExpiresOn
          ? transformDate(convertToUTCDate(signer.utcIdExpiresOn), 'MM/DD/YYYY')
          : '',

        signerPersonalKnowledgeStatement:
          signer.personalKnowledgeStatement ?? '',

        signerWitnessName: signer.witnessName ?? '',
        signerWitnessIdType: signer.witnessIdType ?? '',
        signerWitnessIdInfo: signer.witnessIdInfo ?? '',
      },
      { emitEvent: false },
    );
  }

  /**
   * Applies default signer state and identification method when adding a new signer.
   */
  private patchDefaultsForAdd(): void {
    this.form.patchValue(
      {
        signerState: this.defaultState,
        identificationMethod: 'identification_cards',
      },
      { emitEvent: false },
    );
  }

  /**
   * Builds the DTO for creating a signer from the current form values.
   *
   * @returns Payload for {@link EjournalApiService.addSigner}.
   */
  private buildAddSignerPayload(): Partial<AddEtJournalSignerDto> {
    const v = this.form.getRawValue();
    const method = v.identificationMethod as IdentificationMethod;

    // Fields shared across all identification methods.
    const base: Partial<AddEtJournalSignerDto> = {
      ejournalEntryIds: [],
      // Backend generates the real eJournal signer id; keep a placeholder to satisfy the DTO type.
      name: v.signerName,
      state: v.signerState,
      identificationMethod: method,
      identificationInfo: '',

      // Method-specific fields (set/overwritten below).
      idNumber: '',
      idType: '',
      personalKnowledgeStatement: '',
      witnessName: '',
      witnessIdType: '',
      witnessIdInfo: '',

      address: v.signerAddress,

      thirdPartyId: '',
      thirdPartyVerificationId: '',
      thirdPartyVerificationResult: false,

      ipAddress: this.ipAddress,

      utcIdIssuedOn: '',
      utcIdExpiresOn: '',
      utcSignatureCapture: '',
      signatures: [],
    };

    if (method === 'identification_cards') {
      base.idType = v.signerIdType;
      base.idNumber = v.signerIdNumber;
      base.utcIdIssuedOn = v.signerUtcIdIssuedOn
        ? convertToUTCDate(v.signerUtcIdIssuedOn)
        : '';
      base.utcIdExpiresOn = v.signerUtcIdExpiresOn
        ? convertToUTCDate(v.signerUtcIdExpiresOn)
        : '';
    } else if (method === 'personal_knowledge') {
      base.personalKnowledgeStatement = v.signerPersonalKnowledgeStatement;
    } else if (method === 'credible_witnesses') {
      base.witnessName = v.signerWitnessName;
      base.witnessIdType = v.signerWitnessIdType;
      base.witnessIdInfo = v.signerWitnessIdInfo;
    }

    return base;
  }

  /**
   * Builds the DTO for updating an existing signer, preserving entry IDs from the loaded signer when present.
   *
   * @returns Payload for {@link EjournalApiService.patchSigner}.
   */
  private buildPatchSignerPayload(): Partial<PatchEtJournalSignerDto> {
    const v = this.form.getRawValue();
    const method = v.identificationMethod as IdentificationMethod;

    const ejournalEntryIds =
      this.signer?.ejournalEntryIds && isJSON(this.signer.ejournalEntryIds)
        ? JSON.parse(this.signer.ejournalEntryIds)
        : [];

    const base: Partial<PatchEtJournalSignerDto> = {
      id: this.signer?.eJournalSignerId ?? '',
      ejournalEntryIds,
      name: v.signerName,
      state: v.signerState,
      identificationMethod: method,
      identificationInfo: '',

      idNumber: '',
      idType: '',
      personalKnowledgeStatement: '',
      witnessName: '',
      witnessIdType: '',
      witnessIdInfo: '',

      address: v.signerAddress,

      thirdPartyId: '',
      thirdPartyVerificationId: '',
      thirdPartyVerificationResult: false,

      ipAddress: this.signer?.ipAddress ?? '',

      utcIdIssuedOn: null,
      utcIdExpiresOn: null,
      utcSignatureCapture: '',
    };

    if (method === 'identification_cards') {
      base.idType = v.signerIdType;
      base.idNumber = v.signerIdNumber;
      base.utcIdIssuedOn = v.signerUtcIdIssuedOn
        ? convertToUTCDate(v.signerUtcIdIssuedOn)
        : '';
      base.utcIdExpiresOn = v.signerUtcIdExpiresOn
        ? convertToUTCDate(v.signerUtcIdExpiresOn)
        : '';
    } else if (method === 'personal_knowledge') {
      base.personalKnowledgeStatement = v.signerPersonalKnowledgeStatement;
    } else if (method === 'credible_witnesses') {
      base.witnessName = v.signerWitnessName;
      base.witnessIdType = v.signerWitnessIdType;
      base.witnessIdInfo = v.signerWitnessIdInfo;
    }

    return base;
  }
}
